import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import AdminService from '../services/admin.service';

export default function BoardAdmin() {
  const [tab, setTab] = useState('accesses'); // accesses | invitations | audit
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invitations tab
  const [invitations, setInvitations] = useState([]);
  const [newInvite, setNewInvite] = useState({ email: '', locationId: '' });
  const [inviteLoading, setInviteLoading] = useState(false);

  // Audit tab
  const [execLogs, setExecLogs] = useState([]);
  const [secEvents, setSecEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSub, setAuditSub] = useState('executions'); // executions | security

  useEffect(() => {
    Promise.all([AdminService.getAllUsers(), AdminService.getLocations()])
      .then(([usersRes, locationsRes]) => {
        setUsers(usersRes.data);
        setLocations(locationsRes.data);
      })
      .catch(() => toast.error('Error cargando datos'))
      .finally(() => setLoading(false));
  }, []);

  const selectUser = (user) => {
    setSelectedUser(user);
    setAccesses([]);
    AdminService.getUserAccesses(user.username)
      .then((res) => setAccesses(res.data))
      .catch(() => toast.error('Error cargando accesos del usuario'));
  };

  const handleAssign = (username, locationFunctionId) => {
    AdminService.assignAccess(username, locationFunctionId)
      .then((res) => {
        toast.success(res.data.message);
        return AdminService.getUserAccesses(username);
      })
      .then((res) => setAccesses(res.data))
      .catch((err) => toast.error(err.response?.data?.message || 'Error al asignar acceso'));
  };

  const handleRevoke = (username, userLocationFunctionId) => {
    AdminService.revokeAccess(username, userLocationFunctionId)
      .then((res) => {
        toast.success(res.data.message);
        return AdminService.getUserAccesses(username);
      })
      .then((res) => setAccesses(res.data))
      .catch((err) => toast.error(err.response?.data?.message || 'Error al revocar acceso'));
  };

  const getAccess = (locationFunctionId) =>
    accesses.find((a) => a.locationFunctionId === locationFunctionId);

  // Invitations
  const loadInvitations = () => {
    setInviteLoading(true);
    AdminService.getInvitations()
      .then((res) => setInvitations(res.data))
      .catch(() => toast.error('Error cargando invitaciones'))
      .finally(() => setInviteLoading(false));
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'invitations' && invitations.length === 0) loadInvitations();
    if (t === 'audit' && execLogs.length === 0 && secEvents.length === 0) {
      setAuditLoading(true);
      Promise.all([AdminService.getExecutionLogs(), AdminService.getSecurityEvents()])
        .then(([e, s]) => { setExecLogs(e.data); setSecEvents(s.data); })
        .catch(() => toast.error('Error cargando auditoría'))
        .finally(() => setAuditLoading(false));
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await AdminService.createInvitation(newInvite.email, newInvite.locationId);
      toast.success('Invitación creada');
      setNewInvite({ email: '', locationId: '' });
      setInvitations([res.data, ...invitations]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creando invitación');
    }
  };

  const copyLink = (activationUrl) => {
    const url = `${window.location.origin}${activationUrl}`;
    navigator.clipboard.writeText(url);
    toast.info('Enlace copiado');
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="empty-state"><span className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Panel de administración</h1>
        <p className="dashboard-subtitle">Gestiona vecinos, invitaciones y auditoría</p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button className={`btn-action${tab === 'accesses' ? '' : ' secondary'}`} onClick={() => handleTabChange('accesses')}>
          Accesos
        </button>
        <button className={`btn-action${tab === 'invitations' ? '' : ' secondary'}`} onClick={() => handleTabChange('invitations')}>
          Invitaciones
        </button>
        <button className={`btn-action${tab === 'audit' ? '' : ' secondary'}`} onClick={() => handleTabChange('audit')}>
          Auditoría
        </button>
      </div>

      {/* ── TAB: ACCESOS ── */}
      {tab === 'accesses' && (
        <div className="admin-layout">
          <div className="admin-sidebar">
            <p className="location-title">Vecinos ({users.length})</p>
            {users.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 14 }}>Sin vecinos registrados</p>}
            {users.map((user) => (
              <button
                key={user.id}
                className={`user-item${selectedUser?.id === user.id ? ' active' : ''}`}
                onClick={() => selectUser(user)}
              >
                <span className="user-name">{user.username}</span>
                <span className="user-email">{user.email}</span>
              </button>
            ))}
          </div>

          <div className="admin-main">
            {!selectedUser ? (
              <div className="empty-state">
                <div className="empty-state-icon">👈</div>
                <p>Selecciona un vecino para gestionar sus accesos</p>
              </div>
            ) : (
              <>
                <div className="admin-user-header">
                  <h2 className="admin-user-title">{selectedUser.username}</h2>
                  <span className="admin-user-email">{selectedUser.email}</span>
                </div>
                {locations.map((location) => (
                  <div key={location.id} className="location-card">
                    <p className="location-title">{location.name}</p>
                    <div className="action-grid">
                      {location.functions.length === 0 ? (
                        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Sin funciones configuradas</span>
                      ) : (
                        location.functions.map((func) => {
                          const access = getAccess(func.locationFunctionId);
                          const granted = !!access;
                          return (
                            <button
                              key={func.locationFunctionId}
                              className={`btn-access${granted ? ' granted' : ''}`}
                              onClick={() =>
                                granted
                                  ? handleRevoke(selectedUser.username, access.userLocationFunctionId)
                                  : handleAssign(selectedUser.username, func.locationFunctionId)
                              }
                            >
                              <span className="access-dot" />
                              {func.action || func.name}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: INVITACIONES ── */}
      {tab === 'invitations' && (
        <div className="admin-layout">
          <div className="admin-main" style={{ maxWidth: 520 }}>
            <div className="location-card">
              <p className="location-title">Nueva invitación</p>
              <form onSubmit={handleCreateInvite}>
                <div className="form-group">
                  <label>Email del vecino</label>
                  <input
                    type="email" required value={newInvite.email}
                    onChange={e => setNewInvite(p => ({ ...p, email: e.target.value }))}
                    placeholder="vecino@ejemplo.com"
                  />
                </div>
                <div className="form-group">
                  <label>Comunidad</label>
                  <select
                    required value={newInvite.locationId}
                    onChange={e => setNewInvite(p => ({ ...p, locationId: e.target.value }))}
                    style={{ background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', width: '100%' }}
                  >
                    <option value="" disabled>Selecciona comunidad...</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <button type="submit" className="btn-action" style={{ marginTop: 8 }}>
                  Crear invitación
                </button>
              </form>
            </div>

            <div className="location-card" style={{ marginTop: 16 }}>
              <p className="location-title">Invitaciones enviadas</p>
              {inviteLoading && <div className="empty-state"><span className="spinner" /></div>}
              {!inviteLoading && invitations.length === 0 && (
                <p style={{ color: 'var(--muted)', fontSize: 14 }}>Sin invitaciones aún</p>
              )}
              {invitations.map(inv => (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{inv.email}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 8 }}>{inv.locationName}</span>
                    <span style={{
                      marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 12,
                      background: inv.status === 'USADO' ? 'var(--success-bg, #1a3a2a)' : inv.status === 'EXPIRADO' ? 'var(--error-bg, #3a1a1a)' : 'var(--accent-bg, #1a2a3a)',
                      color: inv.status === 'USADO' ? 'var(--success, #4ade80)' : inv.status === 'EXPIRADO' ? 'var(--error, #f87171)' : 'var(--accent, #60a5fa)'
                    }}>
                      {inv.status === 'USADO' ? 'Activada' : inv.status === 'EXPIRADO' ? 'Expirada' : 'Pendiente'}
                    </span>
                  </div>
                  {inv.status === 'PENDIENTE' && (
                    <button className="btn-action secondary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => copyLink(inv.activationUrl)}>
                      Copiar enlace
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: AUDITORÍA ── */}
      {tab === 'audit' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className={`btn-action${auditSub === 'executions' ? '' : ' secondary'}`} style={{ fontSize: 13 }} onClick={() => setAuditSub('executions')}>
              Ejecuciones
            </button>
            <button className={`btn-action${auditSub === 'security' ? '' : ' secondary'}`} style={{ fontSize: 13 }} onClick={() => setAuditSub('security')}>
              Eventos de seguridad
            </button>
          </div>

          {auditLoading && <div className="empty-state"><span className="spinner" /></div>}

          {!auditLoading && auditSub === 'executions' && (
            <div className="location-card" style={{ overflowX: 'auto' }}>
              {execLogs.length === 0 ? (
                <p style={{ color: 'var(--muted)' }}>Sin registros de ejecución</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                      <th style={{ padding: '6px 12px' }}>Fecha</th>
                      <th style={{ padding: '6px 12px' }}>Usuario</th>
                      <th style={{ padding: '6px 12px' }}>Comunidad</th>
                      <th style={{ padding: '6px 12px' }}>Función</th>
                      <th style={{ padding: '6px 12px' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {execLogs.map(log => (
                      <tr key={log.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 12px', color: 'var(--muted)' }}>{new Date(log.executedAt).toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{log.username}</td>
                        <td style={{ padding: '6px 12px' }}>{log.locationName}</td>
                        <td style={{ padding: '6px 12px' }}>{log.functionName}</td>
                        <td style={{ padding: '6px 12px' }}>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 12,
                            background: log.status === 'SUCCESS' ? 'var(--success-bg, #1a3a2a)' : 'var(--error-bg, #3a1a1a)',
                            color: log.status === 'SUCCESS' ? 'var(--success, #4ade80)' : 'var(--error, #f87171)'
                          }}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!auditLoading && auditSub === 'security' && (
            <div className="location-card" style={{ overflowX: 'auto' }}>
              {secEvents.length === 0 ? (
                <p style={{ color: 'var(--muted)' }}>Sin eventos de seguridad</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                      <th style={{ padding: '6px 12px' }}>Fecha</th>
                      <th style={{ padding: '6px 12px' }}>Evento</th>
                      <th style={{ padding: '6px 12px' }}>Usuario</th>
                      <th style={{ padding: '6px 12px' }}>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {secEvents.map(ev => (
                      <tr key={ev.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 12px', color: 'var(--muted)' }}>{new Date(ev.timestamp).toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{ev.eventType}</td>
                        <td style={{ padding: '6px 12px' }}>{ev.username}</td>
                        <td style={{ padding: '6px 12px', color: 'var(--muted)' }}>{ev.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
